/** @module @liquidefy/code-editor/editor/editor-snippet @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  SnippetDefinition,
  SnippetSession,
  SnippetTransform,
  SnippetVariable,
  TabStop,
  SnippetId,
  LanguageId,
  EditOperation,
  CompletionItem,
  CompletionId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asSnippetId,
  asCompletionId,
  generateId,
  clamp,
  positionToOffset,
  offsetToPosition,
} from '../code-editor.js';

// ═══ §2 SNIPPET PARSING — parse snippet body into AST nodes ═══

/**
 * Node types in the snippet AST.
 */
export type SnippetNodeKind = 'text' | 'tabstop' | 'placeholder' | 'variable' | 'choice' | 'transform';

/**
 * A single node in the parsed snippet AST.
 */
export interface SnippetNode {
  readonly kind: SnippetNodeKind;
  readonly value: string;
  readonly index?: number;
  readonly children?: SnippetNode[];
  readonly variableName?: string;
  readonly choices?: string[];
  readonly transform?: SnippetTransform;
}

/**
 * Parses a snippet body (array of lines joined with \n) into an AST.
 */
export function parseSnippetBody(body: string[]): EditorResult<SnippetNode[]> {
  const text = body.join('\n');
  const nodes: SnippetNode[] = [];
  let i = 0;

  while (i < text.length) {
    if (text[i] === '$') {
      // Potential tabstop, placeholder, or variable
      i++;
      if (i >= text.length) {
        nodes.push({ kind: 'text', value: '$' });
        break;
      }

      if (text[i] === '{') {
        // Complex: ${...}
        i++;
        const closeBrace = findMatchingBrace(text, i - 1);
        if (closeBrace === -1) {
          nodes.push({ kind: 'text', value: '${' });
          continue;
        }

        const inner = text.substring(i, closeBrace);
        const parsed = parseInnerExpression(inner);
        nodes.push(parsed);
        i = closeBrace + 1;
      } else if (isDigit(text[i]!)) {
        // Simple tabstop: $1, $2, etc.
        let numStr = '';
        while (i < text.length && isDigit(text[i]!)) {
          numStr += text[i];
          i++;
        }
        nodes.push({ kind: 'tabstop', value: '', index: parseInt(numStr, 10) });
      } else if (isLetter(text[i]!) || text[i] === '_') {
        // Simple variable: $VAR_NAME
        let name = '';
        while (i < text.length && (isLetterOrDigit(text[i]!) || text[i] === '_')) {
          name += text[i];
          i++;
        }
        nodes.push({ kind: 'variable', value: '', variableName: name });
      } else {
        nodes.push({ kind: 'text', value: '$' });
      }
    } else if (text[i] === '\\' && i + 1 < text.length) {
      // Escaped character
      i++;
      nodes.push({ kind: 'text', value: text[i]! });
      i++;
    } else {
      // Regular text — accumulate
      let textVal = '';
      while (i < text.length && text[i] !== '$' && text[i] !== '\\') {
        textVal += text[i];
        i++;
      }
      if (textVal.length > 0) {
        nodes.push({ kind: 'text', value: textVal });
      }
    }
  }

  return ok(nodes, 'SNIPPET_PARSED', `Parsed ${nodes.length} snippet nodes`);
}

/**
 * Parses a tabstop expression like "1" or "1:placeholder".
 */
export function parseTabStop(raw: string): SnippetNode {
  const colonIdx = raw.indexOf(':');
  if (colonIdx === -1) {
    const idx = parseInt(raw, 10);
    return isNaN(idx)
      ? { kind: 'variable', value: '', variableName: raw }
      : { kind: 'tabstop', value: '', index: idx };
  }
  const idx = parseInt(raw.substring(0, colonIdx), 10);
  const placeholder = raw.substring(colonIdx + 1);
  return { kind: 'placeholder', value: placeholder, index: idx };
}

/**
 * Parses a placeholder expression "1:text".
 */
export function parsePlaceholder(raw: string): SnippetNode {
  const colonIdx = raw.indexOf(':');
  if (colonIdx === -1) return { kind: 'text', value: raw };
  const idx = parseInt(raw.substring(0, colonIdx), 10);
  return { kind: 'placeholder', value: raw.substring(colonIdx + 1), index: idx };
}

/**
 * Parses a variable expression "VAR_NAME" or "VAR_NAME:default".
 */
export function parseVariable(raw: string): SnippetNode {
  const colonIdx = raw.indexOf(':');
  if (colonIdx === -1) {
    return { kind: 'variable', value: '', variableName: raw };
  }
  return {
    kind: 'variable',
    value: raw.substring(colonIdx + 1),
    variableName: raw.substring(0, colonIdx),
  };
}

/**
 * Parses a choice expression "1|one,two,three|".
 */
export function parseChoice(raw: string): SnippetNode {
  const pipeIdx = raw.indexOf('|');
  if (pipeIdx === -1) return { kind: 'text', value: raw };

  const idx = parseInt(raw.substring(0, pipeIdx), 10);
  const rest = raw.substring(pipeIdx + 1);
  const endPipe = rest.lastIndexOf('|');
  const choicesStr = endPipe > 0 ? rest.substring(0, endPipe) : rest;
  const choices = choicesStr.split(',').map((c) => c.trim());

  return { kind: 'choice', value: choices[0] ?? '', index: idx, choices };
}

/**
 * Parses a transform expression "1/regex/replacement/flags".
 */
export function parseTransform(raw: string): SnippetNode {
  const slashIdx = raw.indexOf('/');
  if (slashIdx === -1) return parseTabStop(raw);

  const idx = parseInt(raw.substring(0, slashIdx), 10);
  const rest = raw.substring(slashIdx + 1);
  const parts = splitByUnescapedSlash(rest);

  if (parts.length >= 2) {
    return {
      kind: 'transform',
      value: '',
      index: idx,
      transform: {
        regex: parts[0]!,
        replacement: parts[1]!,
        options: parts[2] ?? '',
      },
    };
  }

  return { kind: 'tabstop', value: '', index: idx };
}

/**
 * Flattens a snippet AST back to plain text (resolving placeholders with their default values).
 */
export function snippetToString(nodes: SnippetNode[]): string {
  let result = '';
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        result += node.value;
        break;
      case 'tabstop':
        break; // tabstops produce no text
      case 'placeholder':
        result += node.value;
        break;
      case 'variable':
        result += node.value; // default value
        break;
      case 'choice':
        result += node.value; // first choice
        break;
      case 'transform':
        break; // transforms produce no inline text
    }
  }
  return result;
}

// ═══ §3 SNIPPET INSERTION — insert into document, resolve variables, compute tab stops ═══

/**
 * Inserts a snippet at a position in the document content.
 * Returns the resulting content, a snippet session for tab stop navigation, and the edits applied.
 */
export function insertSnippet(
  content: string,
  position: Position,
  snippet: SnippetDefinition,
  variables: Record<string, string>,
): EditorResult<{ content: string; session: SnippetSession; edits: EditOperation[] }> {
  const parseResult = parseSnippetBody(snippet.body);
  if (!parseResult.success) return err(parseResult.code, parseResult.message);

  const nodes = parseResult.value;
  const resolved = resolveVariables(nodes, variables, {});
  const expanded = snippetToString(resolved);
  const tabStops = computeTabStops(resolved, position);

  const offset = positionToOffset(content, position);
  const newContent = content.substring(0, offset) + expanded + content.substring(offset);

  const endOffset = offset + expanded.length;
  const endPos = offsetToPosition(newContent, endOffset);

  const edit: EditOperation = {
    kind: 'insert',
    range: { start: position, end: position },
    text: expanded,
    origin: 'snippet',
  };

  const placeholders = new Map<number, string>();
  for (const node of resolved) {
    if ((node.kind === 'placeholder' || node.kind === 'choice') && node.index !== undefined) {
      placeholders.set(node.index, node.value);
    }
  }

  const session: SnippetSession = {
    snippetId: snippet.id,
    tabStops,
    currentTabStop: tabStops.length > 0 ? (tabStops[0]!.index === 0 ? 1 : tabStops[0]!.index) : 0,
    placeholders,
  };

  return ok(
    { content: newContent, session, edits: [edit] },
    'SNIPPET_INSERTED',
    `Snippet ${snippet.name} inserted`,
  );
}

/**
 * Resolves all variable nodes in the AST with their values.
 */
export function resolveVariables(
  nodes: SnippetNode[],
  variables: Record<string, string>,
  _context: Record<string, unknown>,
): SnippetNode[] {
  return nodes.map((node) => {
    if (node.kind === 'variable' && node.variableName) {
      const val = variables[node.variableName];
      if (val !== undefined) {
        return { ...node, value: val };
      }
      // Keep default value if variable not found
      return node;
    }
    return node;
  });
}

/**
 * Expands a snippet body with variable substitution, returning plain text.
 */
export function expandSnippet(
  body: string[],
  variables: Record<string, string>,
): EditorResult<string> {
  const parseResult = parseSnippetBody(body);
  if (!parseResult.success) return err(parseResult.code, parseResult.message);

  const resolved = resolveVariables(parseResult.value, variables, {});
  return ok(snippetToString(resolved), 'SNIPPET_EXPANDED', 'Snippet expanded');
}

/**
 * Computes absolute TabStop positions from parsed snippet nodes at a given insertion point.
 */
export function computeTabStops(
  nodes: SnippetNode[],
  insertPosition: Position,
): TabStop[] {
  const tabStops: TabStop[] = [];
  let currentLine = insertPosition.line;
  let currentCol = insertPosition.column;

  for (const node of nodes) {
    if (node.kind === 'tabstop' || node.kind === 'placeholder' || node.kind === 'choice' || node.kind === 'transform') {
      const startPos: Position = { line: currentLine, column: currentCol };
      const textLen = node.value.length;

      // Advance position through the placeholder text
      let endLine = currentLine;
      let endCol = currentCol;
      for (const ch of node.value) {
        if (ch === '\n') {
          endLine++;
          endCol = 0;
        } else {
          endCol++;
        }
      }

      const endPos: Position = { line: endLine, column: endCol };

      tabStops.push({
        index: node.index ?? 0,
        range: { start: startPos, end: endPos },
        placeholder: node.value || undefined,
        choices: node.choices,
        transform: node.transform,
      });

      currentLine = endLine;
      currentCol = endCol;
    } else if (node.kind === 'text') {
      // Advance position through the text
      for (const ch of node.value) {
        if (ch === '\n') {
          currentLine++;
          currentCol = 0;
        } else {
          currentCol++;
        }
      }
    } else if (node.kind === 'variable') {
      for (const ch of node.value) {
        if (ch === '\n') {
          currentLine++;
          currentCol = 0;
        } else {
          currentCol++;
        }
      }
    }
  }

  // Sort by index, $0 is always last
  return tabStops.sort((a, b) => {
    if (a.index === 0) return 1;
    if (b.index === 0) return -1;
    return a.index - b.index;
  });
}

/**
 * Built-in snippet variables and their descriptions.
 */
export const BUILTIN_VARIABLES: Record<SnippetVariable, string> = {
  'TM_SELECTED_TEXT': 'The currently selected text or empty string',
  'TM_CURRENT_LINE': 'The contents of the current line',
  'TM_CURRENT_WORD': 'The word under cursor or empty string',
  'TM_LINE_INDEX': 'The zero-indexed line number',
  'TM_LINE_NUMBER': 'The one-indexed line number',
  'TM_FILENAME': 'The filename of the current document',
  'TM_FILENAME_BASE': 'The filename without extension',
  'TM_DIRECTORY': 'The directory of the current document',
  'TM_FILEPATH': 'The full file path of the current document',
  'CLIPBOARD': 'The contents of the clipboard',
  'WORKSPACE_NAME': 'The name of the opened workspace',
  'WORKSPACE_FOLDER': 'The path of the opened workspace folder',
  'CURSOR_INDEX': 'The zero-indexed cursor number',
  'CURSOR_NUMBER': 'The one-indexed cursor number',
  'CURRENT_YEAR': 'The current year',
  'CURRENT_MONTH': 'The current month as two digits',
  'CURRENT_DATE': 'The day of the month as two digits',
  'CURRENT_HOUR': 'The current hour in 24-hour clock',
  'CURRENT_MINUTE': 'The current minute as two digits',
  'CURRENT_SECOND': 'The current second as two digits',
  'RANDOM': 'A random 6-digit decimal number',
  'RANDOM_HEX': 'A random 6-digit hex number',
  'UUID': 'A version 4 UUID',
  'BLOCK_COMMENT_START': 'Block comment start for the current language',
  'BLOCK_COMMENT_END': 'Block comment end for the current language',
  'LINE_COMMENT': 'Line comment prefix for the current language',
};

// ═══ §4 TAB STOP NAVIGATION — session management, moving between tab stops ═══

/**
 * Creates a new snippet session for tab stop navigation.
 */
export function createSnippetSession(
  snippetId: SnippetId,
  tabStops: TabStop[],
  placeholders: Map<number, string>,
): SnippetSession {
  // Start at the first non-zero tab stop, or 0 if that's all there is
  const firstNonZero = tabStops.find((ts) => ts.index !== 0);
  const startIndex = firstNonZero ? firstNonZero.index : 0;

  return {
    snippetId,
    tabStops,
    currentTabStop: startIndex,
    placeholders,
  };
}

/**
 * Advances to the next tab stop. Returns updated session.
 */
export function nextTabStop(session: SnippetSession): SnippetSession {
  const sortedIndices = session.tabStops
    .map((ts) => ts.index)
    .filter((idx, i, arr) => arr.indexOf(idx) === i)
    .sort((a, b) => {
      if (a === 0) return 1;
      if (b === 0) return -1;
      return a - b;
    });

  const currentPos = sortedIndices.indexOf(session.currentTabStop);
  const nextPos = currentPos + 1;

  if (nextPos >= sortedIndices.length) {
    // Already at end, jump to $0 if exists
    const zeroStop = session.tabStops.find((ts) => ts.index === 0);
    return { ...session, currentTabStop: zeroStop ? 0 : session.currentTabStop };
  }

  return { ...session, currentTabStop: sortedIndices[nextPos]! };
}

/**
 * Goes back to the previous tab stop.
 */
export function previousTabStop(session: SnippetSession): SnippetSession {
  const sortedIndices = session.tabStops
    .map((ts) => ts.index)
    .filter((idx, i, arr) => arr.indexOf(idx) === i)
    .sort((a, b) => {
      if (a === 0) return 1;
      if (b === 0) return -1;
      return a - b;
    });

  const currentPos = sortedIndices.indexOf(session.currentTabStop);
  const prevPos = currentPos - 1;

  if (prevPos < 0) return session;

  return { ...session, currentTabStop: sortedIndices[prevPos]! };
}

/**
 * Returns the current tab stop data.
 */
export function currentTabStop(session: SnippetSession): TabStop | null {
  return session.tabStops.find((ts) => ts.index === session.currentTabStop) ?? null;
}

/**
 * Returns true if the session is at the last (or $0) tab stop.
 */
export function isLastTabStop(session: SnippetSession): boolean {
  return session.currentTabStop === 0;
}

/**
 * Returns true if the session is at the first non-zero tab stop.
 */
export function isFirstTabStop(session: SnippetSession): boolean {
  const firstNonZero = session.tabStops
    .filter((ts) => ts.index !== 0)
    .sort((a, b) => a.index - b.index)[0];

  return firstNonZero ? session.currentTabStop === firstNonZero.index : true;
}

/**
 * Updates the value of a placeholder at a given tab stop index.
 */
export function updatePlaceholder(
  session: SnippetSession,
  index: number,
  value: string,
): SnippetSession {
  const newPlaceholders = new Map(session.placeholders);
  newPlaceholders.set(index, value);
  return { ...session, placeholders: newPlaceholders };
}

/**
 * Finalizes the snippet session by jumping to the $0 exit point.
 */
export function finalizeSession(session: SnippetSession): SnippetSession {
  return { ...session, currentTabStop: 0 };
}

// ═══ §5 SNIPPET TRANSFORMS — regex, case, and format transforms ═══

/**
 * Applies a snippet transform to text.
 */
export function applySnippetTransform(
  text: string,
  transform: SnippetTransform,
): string {
  return regexTransform(text, transform.regex, transform.replacement, transform.options ?? '');
}

/**
 * Applies a regex-based transform to text.
 */
export function regexTransform(
  text: string,
  regex: string,
  replacement: string,
  flags: string,
): string {
  try {
    const re = new RegExp(regex, flags || 'g');
    return text.replace(re, replacement);
  } catch {
    return text;
  }
}

/**
 * Applies a case transformation to text.
 */
export function caseTransform(
  text: string,
  type: 'upcase' | 'downcase' | 'capitalize' | 'camelCase' | 'pascalCase' | 'snakeCase',
): string {
  switch (type) {
    case 'upcase':
      return text.toUpperCase();
    case 'downcase':
      return text.toLowerCase();
    case 'capitalize':
      return text.charAt(0).toUpperCase() + text.slice(1);
    case 'camelCase': {
      const words = splitWords(text);
      return words.map((w, i) =>
        i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
      ).join('');
    }
    case 'pascalCase': {
      const words = splitWords(text);
      return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    }
    case 'snakeCase': {
      const words = splitWords(text);
      return words.map((w) => w.toLowerCase()).join('_');
    }
  }
}

/**
 * Supported transform flags.
 */
export const TRANSFORM_FLAGS = ['g', 'i', 'm', 's', 'u'] as const;

// ═══ §6 SNIPPET MANAGEMENT — filter, sort, convert, merge, validate ═══

/**
 * Filters snippets by prefix and optional language scope.
 */
export function filterSnippets(
  snippets: SnippetDefinition[],
  prefix: string,
  language?: LanguageId,
): SnippetDefinition[] {
  return snippets.filter((s) => {
    // Check prefix match
    const prefixMatch = s.prefix.some((p) => p.startsWith(prefix));
    if (!prefixMatch) return false;

    // Check language scope
    if (language && s.scope && s.scope.length > 0) {
      return s.scope.includes(language);
    }

    return true;
  });
}

/**
 * Sorts snippets by relevance to a prefix.
 * Exact prefix matches first, then alphabetical.
 */
export function sortSnippets(
  snippets: SnippetDefinition[],
  prefix: string,
): SnippetDefinition[] {
  return [...snippets].sort((a, b) => {
    const aExact = a.prefix.some((p) => p === prefix);
    const bExact = b.prefix.some((p) => p === prefix);
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;

    const aStarts = a.prefix.some((p) => p.startsWith(prefix));
    const bStarts = b.prefix.some((p) => p.startsWith(prefix));
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;

    return a.name.localeCompare(b.name);
  });
}

/**
 * Converts a snippet definition to a completion item for the suggestion list.
 */
export function snippetToCompletionItem(
  snippet: SnippetDefinition,
): CompletionItem {
  return {
    id: asCompletionId(generateId('sc')),
    label: snippet.prefix[0] ?? snippet.name,
    kind: 'snippet',
    detail: snippet.description ?? snippet.name,
    documentation: snippet.body.join('\n'),
    insertText: snippet.body.join('\n'),
    insertTextRules: ['insert-as-snippet'],
    range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
    sortText: snippet.prefix[0],
  };
}

/**
 * Merges multiple snippet sets with user snippets taking precedence over built-in.
 */
export function mergeSnippetSets(
  builtin: SnippetDefinition[],
  user: SnippetDefinition[],
  extension: SnippetDefinition[],
): SnippetDefinition[] {
  const byName = new Map<string, SnippetDefinition>();

  // Built-in first (lowest priority)
  for (const s of builtin) byName.set(s.name, s);
  // Extension overrides built-in
  for (const s of extension) byName.set(s.name, s);
  // User overrides everything
  for (const s of user) byName.set(s.name, s);

  return Array.from(byName.values());
}

/**
 * Validates a snippet definition for syntax correctness.
 */
export function validateSnippet(
  snippet: SnippetDefinition,
): EditorResult<boolean> {
  if (!snippet.name || snippet.name.length === 0) {
    return err('INVALID_NAME', 'Snippet must have a name');
  }

  if (!snippet.prefix || snippet.prefix.length === 0) {
    return err('INVALID_PREFIX', 'Snippet must have at least one prefix');
  }

  if (!snippet.body || snippet.body.length === 0) {
    return err('INVALID_BODY', 'Snippet must have a body');
  }

  // Try parsing to check syntax
  const parseResult = parseSnippetBody(snippet.body);
  if (!parseResult.success) {
    return err('PARSE_ERROR', `Snippet body has syntax errors: ${parseResult.message}`);
  }

  return ok(true, 'VALID', 'Snippet is valid');
}

// ═══ INTERNAL HELPERS ═══

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function isLetter(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z');
}

function isLetterOrDigit(ch: string): boolean {
  return isLetter(ch) || isDigit(ch);
}

/**
 * Finds the matching closing brace for an opening brace at the given index.
 */
function findMatchingBrace(text: string, openIndex: number): number {
  let depth = 1;
  for (let i = openIndex + 1; i < text.length; i++) {
    if (text[i] === '\\' && i + 1 < text.length) {
      i++; // skip escaped character
      continue;
    }
    if (text[i] === '{') depth++;
    if (text[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * Parses the inner expression of a ${...} block.
 */
function parseInnerExpression(inner: string): SnippetNode {
  // Check for choice: 1|one,two|
  if (inner.includes('|')) {
    return parseChoice(inner);
  }

  // Check for transform: 1/regex/replacement/flags
  if (inner.includes('/')) {
    return parseTransform(inner);
  }

  // Check for placeholder: 1:text
  const colonIdx = inner.indexOf(':');
  if (colonIdx !== -1) {
    const before = inner.substring(0, colonIdx);
    if (/^\d+$/.test(before)) {
      return parsePlaceholder(inner);
    }
    // Variable with default: VAR:default
    return parseVariable(inner);
  }

  // Simple tabstop or variable
  if (/^\d+$/.test(inner)) {
    return { kind: 'tabstop', value: '', index: parseInt(inner, 10) };
  }

  return { kind: 'variable', value: '', variableName: inner };
}

/**
 * Splits a string by unescaped forward slashes.
 */
function splitByUnescapedSlash(text: string): string[] {
  const parts: string[] = [];
  let current = '';
  let i = 0;

  while (i < text.length) {
    if (text[i] === '\\' && i + 1 < text.length) {
      current += text[i]! + text[i + 1]!;
      i += 2;
    } else if (text[i] === '/') {
      parts.push(current);
      current = '';
      i++;
    } else {
      current += text[i];
      i++;
    }
  }

  parts.push(current);
  return parts;
}

/**
 * Splits text into words by camelCase, PascalCase, snake_case, and kebab-case boundaries.
 */
function splitWords(text: string): string[] {
  // Split on non-alphanumeric, then split camelCase
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .filter((w) => w.length > 0);
}
